<template>
  <div class="expandable-project-image" @click.stop @keydown.stop>
    <button ref="trigger" type="button" class="project-image-preview image-enlarge image-enlarge-fitted" :aria-label="`Enlarge ${title}`" aria-haspopup="dialog" @click="openViewer">
      <img :src="src" :alt="alt" loading="lazy" decoding="async" />
    </button>
    <Teleport to="body">
      <dialog ref="viewer" class="project-image-viewer" :class="{ 'is-fit': fit }" :aria-label="title" @close="onClose" @click.stop="closeOnBackdrop" @keydown.stop>
        <header class="project-image-viewer-head">
          <span>{{ title }}</span>
          <button type="button" aria-label="Close image" autofocus @click="closeViewer">
            <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" /></svg>
          </button>
        </header>
        <div ref="body" class="project-image-viewer-body" tabindex="0" :aria-label="`${title} enlarged image`">
          <img v-if="viewerOpen" :src="src" :alt="alt" />
        </div>
      </dialog>
    </Teleport>
  </div>
</template>

<script>
import { lockBodyScroll } from '../../utils/bodyScrollLock';
export default {
  name: 'ExpandableProjectImage',
  props: {
    src: { type: String, required: true },
    alt: { type: String, required: true },
    title: { type: String, required: true },
    fit: { type: Boolean, default: false },
  },
  data() { return { viewerOpen: false }; },
  beforeUnmount() { this.onClose(); },
  methods: {
    openViewer() {

      this.viewerOpen = true;
      this._releaseScroll ||= lockBodyScroll();
      this.$refs.viewer.showModal();
      this.$refs.body.scrollTop = 0;
    },
    closeViewer() { this.$refs.viewer.close(); },
    onClose() {
      if (!this.viewerOpen) return;
      this.viewerOpen = false;
      this._releaseScroll?.();this._releaseScroll=null;
      this.$refs.trigger?.focus({ preventScroll: true });
    },
    closeOnBackdrop(event) { if (event.target === this.$refs.viewer) this.closeViewer(); },
  },
};
</script>

<style scoped>
.project-image-preview { position: relative; display: flex; align-items: center; justify-content: center; width: fit-content; max-width: 100%; margin: 0 auto; padding: 0; border: 0; background: none; cursor: var(--site-zoom-in-cursor); }
.project-image-preview img { display: block; width: auto; max-width: 100%; height: auto; max-height: 78svh; margin: 0; border: 0; border-radius: 0; object-fit: contain; }
svg { display: block; width: 18px; height: 18px; fill: none; stroke: currentColor; stroke-width: 1.3; stroke-linecap: round; stroke-linejoin: round; }
button:focus-visible, .project-image-viewer-body:focus-visible { outline: 2px solid currentColor; outline-offset: 4px; }
.project-image-viewer { box-sizing: border-box; position: fixed; inset: 0; width: min(800px, calc(100vw - 32px)); height: calc(100dvh - 32px); max-width: none; max-height: none; padding: 0; border: 0; background: var(--site-bg, #fff); color: var(--site-text-primary, #333); overflow: hidden; }
.project-image-viewer[open] { display: flex; flex-direction: column; }
.project-image-viewer::backdrop { background: rgb(20 20 18 / 75%); }
.project-image-viewer-head { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex: 0 0 auto; padding: 8px 12px 8px 20px; font: 14px/1.4 'Quattrocento Sans', sans-serif; }
.project-image-viewer-head button { display: grid; place-items: center; width: 44px; height: 44px; flex: 0 0 44px; padding: 0; border: 0; background: none; color: inherit; cursor: var(--site-interactive-cursor); }
.project-image-viewer-body { flex: 1; min-height: 0; padding: 0 20px 20px; overflow-y: auto; overscroll-behavior: contain; }
.project-image-viewer-body img { display: block; width: 100%; height: auto; max-height: none; margin: 0; border-radius: 0; }
.project-image-viewer.is-fit { width: calc(100vw - 32px); max-width: 1800px; }
.is-fit .project-image-viewer-body { display: grid; place-items: center; overflow: hidden; }
.is-fit .project-image-viewer-body img { width: 100%; height: 100%; min-height: 0; object-fit: contain; }
:global(body.dark-mode .project-image-viewer) { background: #242426; color: #e2e2e2; }
@media (hover: hover) and (pointer: fine) {
  .project-image-viewer-head button:hover { background: rgb(128 128 128 / 10%); }
}
@media (max-width: 600px) {
  .project-image-viewer { width: 100vw; height: 100dvh; }
  .project-image-viewer-head { padding: 8px 8px 8px 16px; }
  .project-image-viewer-body { padding: 0 8px 8px; }
}
</style>
